import { FatSettingsPageSystemSettingsQuery, FatSettingsPageTenantQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Label from '@/components/basicComponents/label';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { EditInvestorFormFieldsWrapper, EditInvestorModalPageWrapper } from '@/components/investors-page/editInvestorPage/styles';
import {
  CREATE_TENANT_SETTING_OVERRIDE_MUTATION,
  UPDATE_SYSTEM_SETTING_MUTATION,
  UPDATE_TENANT_NAME_MUTATION
} from '@/components/settings-page/queries';
import { reportingTypesEnumMap } from '@/components/settings-page/tenants/constants';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { ROLE } from '@trace-8/trace8-schema';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { basicInfoSchema, IFirmInfoFormData } from '../types';

const reportingTemplateSelectOptions = Object.entries(reportingTypesEnumMap).map(([key, value]) => createSimpleSelectOption(key, value));
enum EditBasicInfoModalPageFlagOptionEnum {
  On = 'On',
  Off = 'Off'
}
const editBasicInfoModalPageFlagOptions = Object.values(EditBasicInfoModalPageFlagOptionEnum);

interface EditBasicInfoModalPageProps {
  basicInfoFormData: IFirmInfoFormData['basicInfo'];
  reportingTypeId: string | null;
  tennantData: {
    id: string;
    name: string;
  };
  showFeatureFlags: boolean;
  featureFlagRefetch: () => Promise<ApolloQueryResult<FatSettingsPageSystemSettingsQuery>>;
  reportingTypeRefetch: () => Promise<ApolloQueryResult<FatSettingsPageSystemSettingsQuery>>;
  tenantNameRefetch: () => Promise<ApolloQueryResult<FatSettingsPageTenantQuery>>;
  handleCloseModalPage: () => void;
}

export const EditBasicInfoModalPage = ({
  basicInfoFormData,
  reportingTypeId,
  tennantData,
  showFeatureFlags,
  featureFlagRefetch,
  reportingTypeRefetch,
  tenantNameRefetch,
  handleCloseModalPage
}: EditBasicInfoModalPageProps) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    control,
    formState: { isSubmitting },
    reset,
    watch,
    handleSubmit
  } = useForm<IFirmInfoFormData['basicInfo']>({
    resolver: zodResolver(basicInfoSchema)
  });

  const isSuperAdmin = user?.roles.includes(ROLE.SUPER_ADMIN);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createTenantSettingOverride] = useMutation(CREATE_TENANT_SETTING_OVERRIDE_MUTATION);
  const [updateSystemSetting] = useMutation(UPDATE_SYSTEM_SETTING_MUTATION);
  const [updateTenantName] = useMutation(UPDATE_TENANT_NAME_MUTATION);

  useEffect(() => {
    if (!basicInfoFormData) return;
    reset(basicInfoFormData);
  }, [basicInfoFormData]);

  const onSubmit = async (data: IFirmInfoFormData['basicInfo']) => {
    try {
      setErrorMessage(null);

      const { firmName, reportingType, internalReviewFlagId, bulkAllocateFlagId, bulkAllocateFlag, internalReviewFlag } = data;

      if (firmName !== tennantData.name) {
        await updateTenantName({
          variables: {
            id: tennantData.id || user?.tenant?.id,
            name: firmName
          }
        });
        await tenantNameRefetch();
      }

      if (basicInfoFormData?.bulkAllocateFlag !== bulkAllocateFlag) {
        const bulkAllocateFlagValue = bulkAllocateFlag ? 'true' : 'false';
        if (bulkAllocateFlagId) {
          await updateSystemSetting({
            variables: {
              data: {
                id: bulkAllocateFlagId,
                value: bulkAllocateFlagValue
              }
            }
          });
        } else {
          await createTenantSettingOverride({
            variables: {
              data: {
                tenantId: tennantData.id || user?.tenant?.id,
                type: 'featureFlag',
                key: 'bulkAllocate',
                value: bulkAllocateFlagValue
              }
            }
          });
        }
      }

      if (basicInfoFormData?.internalReviewFlag !== internalReviewFlag || (!bulkAllocateFlag && internalReviewFlag)) {
        const internalReviewFlagValue = bulkAllocateFlag && internalReviewFlag ? 'true' : 'false';
        if (internalReviewFlagId) {
          await updateSystemSetting({
            variables: {
              data: {
                id: internalReviewFlagId,
                value: internalReviewFlagValue
              }
            }
          });
        } else {
          await createTenantSettingOverride({
            variables: {
              data: {
                tenantId: tennantData.id || user?.tenant?.id,
                type: 'featureFlag',
                key: 'internalReview',
                value: internalReviewFlagValue
              }
            }
          });
        }
      }

      if (basicInfoFormData?.bulkAllocateFlag !== bulkAllocateFlag || basicInfoFormData?.internalReviewFlag !== internalReviewFlag) {
        await featureFlagRefetch();
      }

      // only super admin can set reporting type
      if (isSuperAdmin && basicInfoFormData?.reportingType !== reportingType) {
        const reportingTypeValue = reportingType
          ? JSON.stringify({
              Reporting: reportingType
            })
          : JSON.stringify({ Reporting: '' });

        if (reportingTypeId) {
          await updateSystemSetting({
            variables: {
              data: {
                id: reportingTypeId,
                value: reportingTypeValue
              }
            }
          });
        } else {
          await createTenantSettingOverride({
            variables: {
              data: {
                tenantId: tennantData.id || user?.tenant?.id,
                type: 'appSetting',
                key: 'moduleConfig',
                value: reportingTypeValue
              }
            }
          });
        }
        await reportingTypeRefetch();
      }

      handleCloseModalPage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle="Firm Info" />} />
        <PageTitle title="Basic Info" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <form onSubmit={handleSubmit(onSubmit)}>
            <EditBasicInfoContentWrapper>
              <EditInvestorFormFieldsWrapper>
                <TwoColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="firmName"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input value={value || ''} onChange={onChange} errorText={error?.message} label="Firm Name" size="md" isRequiredField />
                    )}
                  />
                  {isSuperAdmin && (
                    <Controller
                      name="reportingType"
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Select
                          label="Reporting Template"
                          value={value || ''}
                          setValue={onChange}
                          options={[{ label: 'None', value: '' }, ...reportingTemplateSelectOptions]}
                          errorText={error?.message}
                          size="md"
                          isRequiredField
                        />
                      )}
                    />
                  )}
                </TwoColumnWrapper>
                {showFeatureFlags && isSuperAdmin && (
                  <>
                    <div>
                      <Label size="md">Bulk Allocator</Label>
                      <Controller
                        name="bulkAllocateFlag"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <FlagRadioButtonWrapper isMobile={isMobile}>
                            {editBasicInfoModalPageFlagOptions.map((option, index) => (
                              <RadioButton
                                key={option + index}
                                name="bulkAllocateFlag"
                                radioButtonText={option}
                                selectedOption={value ? EditBasicInfoModalPageFlagOptionEnum.On : EditBasicInfoModalPageFlagOptionEnum.Off}
                                handleRadioChange={(event) => onChange(event.target.value === EditBasicInfoModalPageFlagOptionEnum.On)}
                              />
                            ))}
                          </FlagRadioButtonWrapper>
                        )}
                      />
                    </div>
                    <AnimatePresence>
                      {watch('bulkAllocateFlag') && (
                        <motion.div key="biteSize" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <Label size="md">Internal Review</Label>
                          <Controller
                            name="internalReviewFlag"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <FlagRadioButtonWrapper isMobile={isMobile}>
                                {editBasicInfoModalPageFlagOptions.map((option, index) => (
                                  <RadioButton
                                    key={option + index}
                                    name="internalReviewFlag"
                                    radioButtonText={option}
                                    selectedOption={value ? EditBasicInfoModalPageFlagOptionEnum.On : EditBasicInfoModalPageFlagOptionEnum.Off}
                                    handleRadioChange={(event) => onChange(event.target.value === EditBasicInfoModalPageFlagOptionEnum.On)}
                                  />
                                ))}
                              </FlagRadioButtonWrapper>
                            )}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </EditInvestorFormFieldsWrapper>
            </EditBasicInfoContentWrapper>
            <FormFooter onCancel={handleCloseModalPage} disableSaveButton={isSubmitting} showRequiredFieldsWarning={false} />
          </form>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const EditBasicInfoContentWrapper = styled.div`
  padding: 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const FlagRadioButtonWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '0')};
`;
